import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import api from "api/api";
import { useQuery } from "@tanstack/react-query";
import { FIELD_NAMES } from "constants/pricerTable";
import { PRICER_OPT_INFO } from "constants/profile";
import { setDataToLocalStorage } from "helpers/generalHelper";
import { OPTION_ROWS_END_POINT } from "api/pricerApi";
import {
  calendarDate,
  pricerWorkingDate,
  utcToday,
} from "helpers/utcCalendarDate";

// List of initial state for all global data
export const initialState = {
  pricerTablesData: "",
  isLoading: false,
  selectedRows: [],
  dateSelected: null,
  filterButtonSelected: null,
  pricerOptInfo: null,
  filterButtonNames: [],
  pricerRefAgGrid: null,
  isAddOptionSubmitted: false,
  isCellEditing: false,
  isCellLocking: false,
  cellLockingSummaryId: null,
  refetchingRowsAfterCalcDateChange: false,
  isPushingToTeamRunOrAuditTrail: false,
  rowEditingData: null,
  pricerParamsAPI: null,
  isCalculationDateChanged: null,
  secondary_companies_est_columns: [],
  est_columns: { configurable: false, available: [], selection: [] },
  contextMenuAction: null,
  payoutModalSummaryId: null,
  payoutCompareSummaryIds: null,
};

// Actions
const SET_PRICER_DATA = "SET_PRICER_DATA";
const SET_IS_PRICER_FETCHING = "SET_IS_PRICER_FETCHING";
const SET_IS_PRICER_FINISHED_FETCHING = "SET_IS_PRICER_FINISHED_FETCHING";
const SET_SELECTED_ROWS = "SET_SELECTED_ROWS";
const SET_DATE_SELECTED = "SET_DATE_SELECTED";
const SET_FILTER_BUTTON_SELECTED = "SET_FILTER_BUTTON_SELECTED";
const SET_FILTER_BUTTONS_NAME = "SET_FILTER_BUTTONS_NAME";
const SET_PRICER_AG_GRID_REF = "SET_PRICER_AG_GRID_REF";
const SET_IS_ADD_OPTION_SUBMITTED = "SET_IS_ADD_OPTION_SUBMITTED";
const SET_IS_CELL_EDITING = "SET_IS_CELL_EDITING";
const SET_IS_CELL_Locking = "SET_IS_CELL_Locking";
const SET_CELL_Locking_SUMMARY_ID = "SET_CELL_Locking_SUMMARY_ID";
const SET_REFETCHING_ROWS_AFTER_CALC_DATE_CHANGE =
  "SET_REFETCHING_ROWS_AFTER_CALC_DATE_CHANGE";
const SET_IS_PUSHING_TO_TEAM_RUN_OR_AUDIT_TRAIL =
  "SET_IS_PUSHING_TO_TEAM_RUN_OR_AUDIT_TRAIL";
const SET_ROW_EDITING_DATA = "SET_ROW_EDITING_DATA";
const SET_PRICER_PARAMS = "SET_PRICER_PARAMS";
const SET_PRICER_OPERATIONS = "SET_PRICER_OPERATIONS";
const SET_IS_CALCULATION_DATE_CHANGED = "SET_IS_CALCULATION_DATE_CHANGED";
const SET_SECONDARY_COMPANIES_EST_COLUMNS = "SET_SECONDARY_COMPANIES_EST_COLUMNS";
const SET_EST_COLUMNS = "SET_EST_COLUMNS";
const SET_CONTEXT_MENU_ACTION = "SET_CONTEXT_MENU_ACTION";
const SET_PAYOUT_MODAL_SUMMARY_ID = "SET_PAYOUT_MODAL_SUMMARY_ID";
const SET_PAYOUT_COMPARE_SUMMARY_IDS = "SET_PAYOUT_COMPARE_SUMMARY_IDS";

// Shared Reducer For Global Context
const Reducer = (state, action) => {
  switch (action.type) {
    case SET_IS_PRICER_FETCHING:
      return {
        ...state,
        isLoading: true,
      };
    case SET_IS_CALCULATION_DATE_CHANGED:
      return {
        ...state,
        isCalculationDateChanged: action.payload,
      };
    case SET_SELECTED_ROWS:
      return {
        ...state,
        selectedRows: action.payload,
      };
    case SET_IS_PRICER_FINISHED_FETCHING:
      return {
        ...state,
        isLoading: false,
      };
    case SET_FILTER_BUTTON_SELECTED:
      return {
        ...state,
        filterButtonSelected: action.payload,
      };
    case SET_PRICER_AG_GRID_REF:
      return {
        ...state,
        pricerRefAgGrid: action.payload,
      };
    case SET_PRICER_OPERATIONS:
      return {
        ...state,
        pricerOptInfo: action.payload,
      };
    case SET_FILTER_BUTTONS_NAME:
      return {
        ...state,
        filterButtonNames: action.payload,
      };
    case SET_DATE_SELECTED:
      return {
        ...state,
        dateSelected: action.payload,
      };
    case SET_PRICER_DATA:
      return {
        ...state,
        pricerTablesData: action.payload,
        isLoading: false,
      };
    case SET_IS_ADD_OPTION_SUBMITTED:
      return {
        ...state,
        isAddOptionSubmitted: action.payload,
      };
    case SET_IS_CELL_EDITING:
      return {
        ...state,
        isCellEditing: action.payload,
      };
    case SET_IS_CELL_Locking:
      return {
        ...state,
        isCellLocking: action.payload,
      };
    case SET_CELL_Locking_SUMMARY_ID:
      return {
        ...state,
        cellLockingSummaryId: action.payload,
      };
    case SET_REFETCHING_ROWS_AFTER_CALC_DATE_CHANGE:
      return {
        ...state,
        refetchingRowsAfterCalcDateChange: action.payload,
      };
    case SET_IS_PUSHING_TO_TEAM_RUN_OR_AUDIT_TRAIL:
      return {
        ...state,
        isPushingToTeamRunOrAuditTrail: action.payload,
      };
    case SET_ROW_EDITING_DATA:
      return {
        ...state,
        rowEditingData: action.payload,
      };
    case SET_PRICER_PARAMS:
      return {
        ...state,
        pricerParamsAPI: action.payload,
      };
    case SET_SECONDARY_COMPANIES_EST_COLUMNS:
      return {
        ...state,
        secondary_companies_est_columns: action.payload,
      };
    case SET_EST_COLUMNS:
      return {
        ...state,
        est_columns: action.payload,
      };
    case SET_CONTEXT_MENU_ACTION:
      return {
        ...state,
        contextMenuAction: action.payload,
      };
    case SET_PAYOUT_MODAL_SUMMARY_ID:
      return {
        ...state,
        payoutModalSummaryId: action.payload,
      };
    case SET_PAYOUT_COMPARE_SUMMARY_IDS:
      return {
        ...state,
        payoutCompareSummaryIds: action.payload,
      };
    default:
      return state;
  }
};

// Global State Which its provide context for children
const PricerState = ({ children, defaultInitialState = {} }) => {
  const [state, dispatch] = useReducer(Reducer, {
    ...initialState,
    dateSelected: utcToday(),
    isCalculationDateChanged: 1,
    ...defaultInitialState,
  });

  const setPricerData = useCallback((value) => {
    dispatch({ type: SET_PRICER_DATA, payload: value });
  }, []);

  const setSecondaryCompaniesEstColumns = useCallback((value) => {
    dispatch({ type: SET_SECONDARY_COMPANIES_EST_COLUMNS, payload: value });
  }, []);

  const setEstColumns = useCallback((value) => {
    dispatch({ type: SET_EST_COLUMNS, payload: value });
  }, []);

  const setContextMenuAction = useCallback((value) => {
    dispatch({ type: SET_CONTEXT_MENU_ACTION, payload: value });
  }, []);

  const openPayoutModal = useCallback((summaryId) => {
    dispatch({ type: SET_PAYOUT_MODAL_SUMMARY_ID, payload: summaryId ?? null });
  }, []);

  const closePayoutModal = useCallback(() => {
    dispatch({ type: SET_PAYOUT_MODAL_SUMMARY_ID, payload: null });
  }, []);

  const openPayoutCompareModal = useCallback((summaryIds) => {
    const ids = Array.isArray(summaryIds)
      ? summaryIds.filter((id) => id != null)
      : [];
    dispatch({
      type: SET_PAYOUT_COMPARE_SUMMARY_IDS,
      payload: ids.length ? ids : null,
    });
  }, []);

  const closePayoutCompareModal = useCallback(() => {
    dispatch({ type: SET_PAYOUT_COMPARE_SUMMARY_IDS, payload: null });
  }, []);

  const setSelectedRowsDispatch = useCallback((value) => {
    dispatch({ type: SET_SELECTED_ROWS, payload: value });
  }, []);

  const setDateSelected = useCallback((value) => {
    dispatch({
      type: SET_DATE_SELECTED,
      payload: pricerWorkingDate(value),
    });
  }, []);

  const setIsOptionRowLoading = useCallback((value) => {
    if (value) {
      dispatch({ type: SET_IS_PRICER_FETCHING, payload: value });
      return;
    }
    dispatch({ type: SET_IS_PRICER_FINISHED_FETCHING, payload: value });
  }, []);

  const setFilterButtonSelected = useCallback((value) => {
    dispatch({ type: SET_FILTER_BUTTON_SELECTED, payload: value });
  }, []);

  const setIsCalculationDateChanged = useCallback((value) => {
    dispatch({ type: SET_IS_CALCULATION_DATE_CHANGED, payload: value });
  }, []);

  const setPricerOperation = useCallback((value) => {
    dispatch({ type: SET_PRICER_OPERATIONS, payload: value });
  }, []);

  const setFilterButtonNames = useCallback((value) => {
    dispatch({ type: SET_FILTER_BUTTONS_NAME, payload: value });
  }, []);

  const setPricerAgGridRef = useCallback((value) => {
    dispatch({ type: SET_PRICER_AG_GRID_REF, payload: value });
  }, []);

  const setIsAddOptionSubmitted = useCallback((value) => {
    dispatch({ type: SET_IS_ADD_OPTION_SUBMITTED, payload: value });
  }, []);

  const setIsCellEditing = useCallback((value) => {
    dispatch({ type: SET_IS_CELL_EDITING, payload: value });
  }, []);

  const setIsCellLocking = useCallback((value) => {
    dispatch({ type: SET_IS_CELL_Locking, payload: value });
  }, []);

  const setCellLockingSummaryId = useCallback((value) => {
    dispatch({ type: SET_CELL_Locking_SUMMARY_ID, payload: value });
  }, []);

  const setIsPushingToTeamRunOrAuditTrail = useCallback((value) => {
    dispatch({
      type: SET_IS_PUSHING_TO_TEAM_RUN_OR_AUDIT_TRAIL,
      payload: value,
    });
  }, []);

  const setRefetchingRowsAfterCalcDateChange = useCallback((value) => {
    dispatch({
      type: SET_REFETCHING_ROWS_AFTER_CALC_DATE_CHANGE,
      payload: value,
    });
  }, []);

  const setRowEditingData = useCallback((value) => {
    dispatch({ type: SET_ROW_EDITING_DATA, payload: value });
  }, []);

  const setPricerParams = useCallback((value) => {
    dispatch({ type: SET_PRICER_PARAMS, payload: value });
  }, []);

  const { refetch, isFetching, isLoading ,isRefetching} = useQuery(
    ["option-rows", state.dateSelected],
    async () => {
      // calculation_date is required by the backend, so it's always sent —
      // never send the request without it. This call also saves the
      // preference Push to Team Run checks against the UTC day.
      const params = {
        [FIELD_NAMES.CALCULATION_DATE]: pricerWorkingDate(state.dateSelected),
      };
      return api
        .get(`${OPTION_ROWS_END_POINT}`, { ...params })
        .then((res) => res.data.data)
        .catch((err) => {
          console.error({ err });
          throw err;
        });
    },
    {
      enabled: !!state.dateSelected,
      retryOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      // Was `retry: true` (up to 3 retries = 4 requests per failure,
      // including retrying permanent 4xx errors that can never succeed).
      // One retry is enough to ride out a transient network/5xx blip, and we
      // skip it entirely for 4xx since retrying won't change the outcome.
      retry: (failureCount, error) => {
        const status = error?.response?.status;
        if (status >= 400 && status < 500) return false;
        return failureCount < 1;
      },
      retryDelay: 1000,
      onSuccess: (successItem) => {
        setPricerData(successItem?.rows);
        setSecondaryCompaniesEstColumns(successItem?.secondary_companies_est_columns);
        setEstColumns(successItem?.est_columns);
        const resolvedDate = calendarDate(successItem?.calculation_date);
        // Only dispatch when the backend actually resolved to a different
        // date (e.g. weekend/holiday adjustment) — avoids an extra refetch.
        // Keep the YYYY-MM-DD as sent. Parsing it as a local Date moves the
        // day across midnight.
        if (resolvedDate && resolvedDate !== state.dateSelected) {
          setDateSelected(resolvedDate);
        }
        return successItem;
      },
    }
  );

  useEffect(() => {
    setIsOptionRowLoading(isFetching);
    setRefetchingRowsAfterCalcDateChange(isFetching);
  }, [isFetching]);

  useEffect(() => {
    if (!state.pricerOptInfo) return;
    setDataToLocalStorage(PRICER_OPT_INFO, state.pricerOptInfo);
  }, [state.pricerOptInfo]);

  const contextValue = useMemo(() => {
    return {
      state,
      refetchOptionRow: refetch,
      isOptionRowFetching: isFetching,
      isOptionRowLoading: isLoading,
      isOptionRowRefetching: isRefetching,
      setSelectedRowsDispatch,
      setIsPushingToTeamRunOrAuditTrail,
      setPricerData,
      setFilterButtonSelected,
      setDateSelected,
      setFilterButtonNames,
      setPricerAgGridRef,
      setIsAddOptionSubmitted,
      setIsCellEditing,
      setIsCellLocking,
      setCellLockingSummaryId,
      setPricerParams,
      setRowEditingData,
      setPricerOperation,
      setIsOptionRowLoading,
      setIsCalculationDateChanged,
      setSecondaryCompaniesEstColumns,
      setEstColumns,
      setContextMenuAction,
      openPayoutModal,
      closePayoutModal,
      openPayoutCompareModal,
      closePayoutCompareModal,
    };
  }, [state]);

  return (
    <PricerContext.Provider value={contextValue}>
      {children}
    </PricerContext.Provider>
  );
};

// Create Global Context
export const PricerContext = createContext(initialState);

// Export Global State Context Component
export default PricerState;
